/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment'
import { Divider, Form } from 'antd'
import { CustomLink } from 'src/components'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { showNotification } from 'src/core/helpers'
import BrokerageErrorModal from './BrokerageErrorModal/BrokerageErrorModal'
import { useAppDispatch, useAppSelector, useWindowSize } from 'src/app/hooks'
import ButtonsContent from './StockPurchaseForm/ButtonsContent/ButtonsContent'
import RegisterUserModal from './StockPurchaseForm/RegisterUserModal/RegisterUserModal'
import { ProjectAcquisitionFormPayload, ProjectAcquisitionPayload } from 'src/core/models'
import { acquisitionSections as sections, resetAcquisitionForm } from './acquisition-sections'
import ProjectRegisterModal from './StockPurchaseForm/ProjectRegisterModal/ProjectRegisterModal'
import {
  purchasePriceCurrency, salePriceCurrency,
  validateUserEmail, resetValidateEmailState,
  createPurchaseOperation, selectedBrokerageTab,
  registerBrokerageUserSelector, createBrokerageProject,
  validateEmailSelector, validateProjectIdSelector,
  createPurchaseOperationSelector,
} from 'src/features'

const StockPurchaseTabContent: React.FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { isMobile } = useWindowSize()

  /* Component States */
  const [isCommitment, setIsCommitment] = useState(false)
  const [showTableLink, setShowTableLink] = useState(false)
  const [isProjectRegisterModalVisible, setIsProjectRegisterModalVisible] = useState(false)
  const [isUserRegisterModalVisible, setIsUserRegisterModalVisible] = useState(false)
  const [isBrokerageModalVisible, setIsBrokerageModalVisible] = useState(false)
  const [stockRestFlag, setStockRestFlag] = useState<'REST' | 'OUTSIDE_PLATFORM'>('REST')
  const [emailAux, setEmailAux] = useState(null)

  /* Selectors */
  const purchasePriceCurrencyValue = useAppSelector(purchasePriceCurrency)
  const salePriceCurrencyValue = useAppSelector(salePriceCurrency)
  const { email } = useAppSelector(registerBrokerageUserSelector)
  const { id: projectId } = useAppSelector(createBrokerageProject)
  const selectedTab = useAppSelector(selectedBrokerageTab)

  /* Validate Email Selector */
  const {
    error: errorEmailValidation
  } = useAppSelector(validateEmailSelector)

  /* Validate Project Id Selector */
  const {
    response: validateProjectIdResponse,
    project
  } = useAppSelector(validateProjectIdSelector)

  /* Submit Operation Selector */
  const {
    loading: loadingOperation,
    success: successOperation,
    error: errorCreatePurchaseOperation
  } = useAppSelector(createPurchaseOperationSelector)

  const isUserEmailInvalid = ![null, 'SUCCESS'].includes(errorEmailValidation)
  const projectIdHasError = ['ERROR'].includes(validateProjectIdResponse)

  /* Use Effects */
  useEffect(() => form.setFieldsValue({ 'projectId': projectId }), [projectId])

  useEffect(() => {
    if (selectedTab === 'PURCHASE' && email) {
      form.setFieldsValue({ 'sellerEmail': email })
      dispatch(resetValidateEmailState())
    }
  }, [email])

  useEffect(() => {
    if (successOperation) {
      setShowTableLink(true)
      resetForm(false)
      showNotification('success', t('admin.Brokerage.SuccessOperation'))
    }
  }, [successOperation])

  useEffect(() => {
    if (errorCreatePurchaseOperation?.type) { setIsBrokerageModalVisible(true) }
  }, [errorCreatePurchaseOperation])

  useEffect(() => (() => resetForm(false)), [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value
    setEmailAux(email)
    if (email.trim() === '') { dispatch(resetValidateEmailState()) }
    else { dispatch(validateUserEmail(email)) }
  }

  /* Reset All Form and states */
  const resetForm = (showResetMessage = true) => {
    resetAcquisitionForm(
      dispatch, setStockRestFlag, form,
      showResetMessage, setShowTableLink, t,
      setIsCommitment
    )
  }

  /* Handle Project Acquisition Submit */
  const onFinish = (values: ProjectAcquisitionFormPayload) => {
    if (project?.is_vintage && project?.vintage_items?.length === 0) return

    if (projectIdHasError || isUserEmailInvalid) {
      showNotification('error', t('admin.Brokerage.CheckProjecIdAndEmail'))
      return
    }
    const params: ProjectAcquisitionPayload = {
      ...values,
      acquisitionDate: values.acquisitionDate ? moment(values.acquisitionDate).format('YYYY-MM-DD') : null,
      stockRestType: stockRestFlag,
      purchasePriceCurrency: purchasePriceCurrencyValue,
      salePriceCurrency: salePriceCurrencyValue,
      is_commitment: isCommitment
    }
    setShowTableLink(false)
    dispatch(createPurchaseOperation(params))
  }

  return <>
    {isMobile && <Divider />}
    <Form
      name='basic'
      onFinish={onFinish}
      autoComplete='off'
      form={form}
    >
      {/* Seller User */}
      <sections.SellerUserAcquisition
        handleEmailChange={handleEmailChange}
        isUserEmailInvalid={isUserEmailInvalid}
        errorEmailValidation={errorEmailValidation}
        setIsUserRegisterModalVisible={(value) => setIsUserRegisterModalVisible(value)}
      />
      {/* Project Id */}
      <Divider />

      <sections.ProjectIdAcquisition
        form={form}
        emailAux={emailAux}
        projectIdHasError={projectIdHasError}
        isUserEmailInvalid={isUserEmailInvalid}
        setIsProjectRegisterModalVisible={(value) => setIsProjectRegisterModalVisible(value)}
      />

      {/* Stock Fields */}
      <sections.AcquisitionStockField />

      {/* Checkboxes */}
      <sections.AcquisitionCheckbox
        stockRestFlag={stockRestFlag}
        setStockRestFlag={(value) => setStockRestFlag(value)}
      />

      <Divider />

      {/* Acquisition Prices */}
      <sections.AcquisitionPrices />

      {/* Acquisition Date */}
      <sections.AcquisitionDateForm />

      {/* Is Commitment */}
      <sections.AcquisitionIsCommitment
        isCommitment={isCommitment}
        setIsCommitment={setIsCommitment}
      />

      {/* Flag Stock */}
      <Divider />
      <ButtonsContent handleResetForm={resetForm} loading={loadingOperation} />

      {/* Results Link */}
      {showTableLink && <CustomLink
        link={`${process.env.BACKEND_ADMIN_URL}/projects/projectacquisitions/`}
        label={t('admin.Brokerage.LinkLabel')}
      />}
    </Form>

    <ProjectRegisterModal
      isModalVisible={isProjectRegisterModalVisible}
      setIsModalVisible={setIsProjectRegisterModalVisible}
      email={form.getFieldValue('sellerEmail')}
    />
    <RegisterUserModal
      isModalVisible={isUserRegisterModalVisible}
      setIsModalVisible={setIsUserRegisterModalVisible}
    />
    <BrokerageErrorModal
      isModalVisible={isBrokerageModalVisible}
      setIsModalVisible={setIsBrokerageModalVisible}
      error={errorCreatePurchaseOperation}
    />
  </>
}

export default StockPurchaseTabContent
